import { useEffect, useState, useContext, useRef } from 'react';
import { StateContext } from '../../../Controllers/context/state';
import { Container, TickerInfo, StockChartContainer } from './styled-components';
import Loading from '../LoadingSpinner';
import StockChart from './components/StockChart/StockChart';
import StockChartCarousel from './components/StockChartCarousel/StockChartCarousel';
import ChartFilters from './components/ChartFilters/ChartFilters';
import { capitalizeFirstLetter } from '../../../Controllers/utils';
import { Input } from '../../StyledComponents/UserFormStyledComponents';
import { useStockData } from './hooks/useStockData';
import { useChartFilters } from './hooks/useChartFilters';

const StockChartWrapper = ({ assetPage, assets, dateDisplay, handleDateChange, handleNavigation, isDelisted, lastDate, setLastDate, selectedDate, symbol, width }) => {
    const [carouselPlotLine, setCarouselPlotline] = useState(null);
    const [chartMinMax, setChartMinMax] = useState({ min: 0, max: 0 });
    const { chartType, setChartType, setShowChartInfoWindow } = useContext(StateContext);
    const [clickedPoint, setClickedPoint] = useState(null);
    const [hoveredPoint, setHoverPoint] = useState(null);
    const stockChartWrapperRef = useRef(null);

    // Custom hooks
    const { loading, tickerData, historicalData } = useStockData(symbol, chartType, selectedDate);
    const {
        selectedInfoScore,
        selectedPercentile,
        filteredHistoricalData,
        eventTypes,
        selectedEventType,
        isFiltersOpen,
        isEventTypesOpen,
        eventTypeSearch,
        setSelectedEventType,
        setIsFiltersOpen,
        setIsEventTypesOpen,
        setEventTypeSearch,
        handleChangePercentile,
        handleChangeInfoScore
    } = useChartFilters(historicalData, chartType, symbol);

    const formatSectorAndType = (gsector, type) => {
        const formattedGsector = capitalizeFirstLetter(gsector);

        let formattedType = type;
        if (type !== "ETF" && type !== "ETP") {
            formattedType = capitalizeFirstLetter(type);
        }

        if (formattedGsector && formattedType) {
            return `${formattedGsector}, ${formattedType}`;
        } else {
            return formattedGsector || formattedType || '';
        }
    };

    // Reset state on symbol/chart type/filter changes
    useEffect(() => {
        setClickedPoint(null);
        setHoverPoint(null);
        setCarouselPlotline(null);
        setShowChartInfoWindow(false);
    }, [chartType, symbol, selectedPercentile, selectedInfoScore, selectedEventType, setShowChartInfoWindow]);


    return (
        <StockChartContainer ref={stockChartWrapperRef} width={width}>
            {
                loading ?
                    <Loading /> :
                    <>
                        <Container>
                            <TickerInfo>
                                <h1>{tickerData.name}</h1>
                                <h2>
                                    {formatSectorAndType(tickerData?.gsector, tickerData?.type)}
                                </h2>
                            </TickerInfo>
                            {selectedEventType !== 'All' && (
                                <TickerInfo style={{ justifyContent: 'center', alignItems: 'center' }}>
                                    <h2>
                                        Showing {selectedEventType} events
                                    </h2>
                                </TickerInfo>
                            )}
                            <TickerInfo style={{ flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
                                <div style={{ width: '135px' }}>
                                    <Input
                                        min={new Date("15 February, 2023").toISOString().split("T")[0]}
                                        max={new Date().toISOString().split("T")[0]}
                                        onChange={handleDateChange}
                                        style={{ height: '44px', width: '100%', fontSize: '13px' }}
                                        type="date"
                                        value={dateDisplay}
                                    />
                                </div>
                                <ChartFilters
                                    isFiltersOpen={isFiltersOpen}
                                    setIsFiltersOpen={setIsFiltersOpen}
                                    chartType={chartType}
                                    setChartType={setChartType}
                                    selectedPercentile={selectedPercentile}
                                    handleChangePercentile={handleChangePercentile}
                                    selectedInfoScore={selectedInfoScore}
                                    handleChangeInfoScore={handleChangeInfoScore}
                                    eventTypes={eventTypes}
                                    selectedEventType={selectedEventType}
                                    setSelectedEventType={setSelectedEventType}
                                    isEventTypesOpen={isEventTypesOpen}
                                    setIsEventTypesOpen={setIsEventTypesOpen}
                                    eventTypeSearch={eventTypeSearch}
                                    setEventTypeSearch={setEventTypeSearch}
                                />
                            </TickerInfo>

                        </Container>
                        <>
                            <StockChart
                                carouselPlotLine={carouselPlotLine}
                                height={stockChartWrapperRef?.current?.offsetHeight - 20}
                                hoverPoint={hoveredPoint}
                                selectedDate={selectedDate}
                                setChartMinMax={setChartMinMax}
                                setClickedPoint={setClickedPoint}
                                setHoverPoint={setHoverPoint}
                                symbol={symbol.toUpperCase()}
                                width={width}
                                historicalData={historicalData}
                                selectedPercentile={selectedPercentile}
                                selectedInfoScore={selectedInfoScore}
                                selectedEventType={selectedEventType}
                            />
                            <StockChartCarousel
                                chartMinMax={chartMinMax}
                                clickedPoint={clickedPoint}
                                hoveredPoint={hoveredPoint}
                                setCarouselPlotline={setCarouselPlotline}
                                setHoverPoint={setHoverPoint}
                                historicalData={filteredHistoricalData}
                                symbol={symbol.toUpperCase()}
                            />
                        </>
                    </>
            }
        </StockChartContainer>
    );
};

export default StockChartWrapper;
